import React, { useState } from 'react';
import { auth, db } from '../firebase'; 
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  sendEmailVerification, 
  GoogleAuthProvider,
  signInWithPopup 
} from "firebase/auth";
import { doc, setDoc, collection, addDoc, serverTimestamp, getDoc, updateDoc } from "firebase/firestore";

const Login = () => {
  const [view, setView] = useState('login'); 
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const syncUserProfile = async (user, isNewUser) => {
    if (!user?.uid || !user?.email) return;
    const userRef = doc(db, "competitors", user.uid);
    try {
      if (isNewUser) {
        // Initial registration (unverified)
        await setDoc(userRef, {
          email: user.email,
          role: "competitor",
          createdAt: serverTimestamp(),
          fullName: user.displayName || email.split('@')[0], 
          status: "Active",
          welcomeSent: false,
          profileCompleted: false 
        });
      } else {
        const docSnap = await getDoc(userRef);
        // TRIGGER WELCOME EMAIL: Only if verified and haven't sent it yet
        if (docSnap.exists() && user.emailVerified && !docSnap.data().welcomeSent) {
          
          const emailHtml = `
            <div style="font-family: 'Poppins', sans-serif; max-width: 600px; margin: auto; border: 1px solid #eee; border-radius: 15px; overflow: hidden;">
              <div style="background-color: #1a1a1a; padding: 40px; text-align: center;">
                <h1 style="color: #ffffff; margin: 0; text-transform: uppercase;">Welcome to the Grid</h1>
                <p style="color: #ce1126; font-weight: bold; margin-top: 10px; letter-spacing: 2px;">REGISTRATION VERIFIED</p>
              </div>
              <div style="padding: 40px; color: #333; line-height: 1.6;">
                <p>Hello Driver,</p>
                <p>Your account for the <strong>Adrenaline Motorsport SL Portal</strong> has been successfully verified.</p>
                <p>Please complete your <strong>Competitor Passport</strong> to start registering for events and managing your digital garage.</p>
                <div style="text-align: center; margin: 30px 0;">
                  <a href="https://slardar-portal.web.app" style="background-color: #ce1126; color: white; padding: 12px 25px; text-decoration: none; border-radius: 50px; font-weight: bold; display: inline-block;">ACCESS DASHBOARD</a>
                </div>
                <p>See you on the track!</p>
              </div>
            </div>
          `;

          await addDoc(collection(db, "mail"), {
            to: user.email,
            message: {
              subject: "🏁 Welcome to Adrenaline Motorsport SL!",
              html: emailHtml
            },
          });

          await updateDoc(userRef, { welcomeSent: true });
        }
      }
    } catch (err) { console.error("Sync Error:", err); }
  };

  const handleAuth = async (e) => {
    e.preventDefault();
    setError(''); setMessage(''); setLoading(true);

    try {
      if (view === 'register') {
        if (password.length < 6) throw new Error("Password should be at least 6 characters.");

        // 1. Create the account
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        const newUser = userCredential.user;

        // 2. Sync Profile
        await syncUserProfile(newUser, true);

        // 3. Send Email Verification with Redirect
        const actionCodeSettings = {
          url: 'https://slardar-portal.web.app', 
          handleCodeInApp: true,
        };
        await sendEmailVerification(newUser, actionCodeSettings);

        // 4. Safety Delay for Firestore sync
        await new Promise(resolve => setTimeout(resolve, 800));

        // 5. Sign out to force verification loop
        await auth.signOut();

        // 6. Set Formal Success Message
        setMessage(
          "Account Created Successfully! 🏁 To complete your registration and access the dashboard, " +
          "please verify your email address using the link sent to your inbox."
        );
        
        setView('login');
        setEmail(''); 
        setPassword('');

      } else if (view === 'login') {
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        
        if (!userCredential.user.emailVerified) {
          setError("Access Denied: Please verify your email address to continue.");
          await auth.signOut();
          return;
        }
        await syncUserProfile(userCredential.user, false);

      } else if (view === 'forgot') {
        await sendPasswordResetEmail(auth, email);
        setMessage("Password reset link sent! Check your inbox.");
        setView('login');
      }
    } catch (err) {
      setError(err.message.replace("Firebase: ", "").replace(/\(auth.*\)\.?/, "").trim());
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    const provider = new GoogleAuthProvider();
    try {
      setLoading(true);
      const result = await signInWithPopup(auth, provider);
      const docSnap = await getDoc(doc(db, "competitors", result.user.uid));
      
      if (!docSnap.exists()) {
        await syncUserProfile(result.user, true);
        await syncUserProfile(result.user, false);
      } else {
        await syncUserProfile(result.user, false);
      }
    } catch (err) { setError("Google Sign-In failed."); } finally { setLoading(false); }
  };

  return (
    <div className="min-vh-100 d-flex align-items-center justify-content-center theme-poppins page-zoom-container" 
         style={{ background: 'radial-gradient(circle at center, #1a1a1a 0%, #000000 100%)', backgroundAttachment: 'fixed', width: '100%' }}>
      <div className="card shadow-lg border-0 p-4 p-md-5 rounded-4 animate-fade-in" 
           style={{ maxWidth: '440px', width: '92%', backgroundColor: '#ffffff' }}>
        
        <div className="text-center mb-4">
          <img src="/logo.jpg" alt="Adrenaline Motorsport" className="mb-3" style={{ maxHeight: '65px', objectFit: 'contain' }} />
          <div className="badge bg-dark px-3 mb-2 rounded-pill tracking-widest normal-text" style={{ color: '#ce1126' }}>RACE MANAGEMENT PORTAL</div>
          <h1 className="main-header text-dark mb-0 text-uppercase">
            {view === 'register' ? 'Join the Grid' : view === 'forgot' ? 'Reset Password' : 'Welcome Driver'}
          </h1>
        </div>

        {error && <div className="alert alert-danger normal-text py-2 mb-3 fw-bold" style={{ fontSize: '13px' }}>{error}</div>}
        {message && <div className="alert alert-success normal-text py-2 mb-3 fw-bold" style={{ fontSize: '13px' }}>{message}</div>}

        <form onSubmit={handleAuth}>
          <div className="mb-3 text-start">
            <label className="form-label normal-text fw-bold text-dark opacity-75 text-uppercase mb-2" style={{ fontSize: '12px' }}>
              {view === 'register' ? 'Choose Username (Email)' : 'Email / Username'}
            </label>
            <div className="input-group border rounded-3 overflow-hidden shadow-sm">
              <span className="input-group-text bg-light border-0"><i className="bi bi-envelope text-dark"></i></span>
              <input type="email" className="form-control border-0 shadow-none py-2 normal-text" value={email} 
                     onChange={(e) => setEmail(e.target.value)} disabled={loading} required placeholder="driver@email.com" />
            </div>
          </div>

          {view !== 'forgot' && (
            <div className="mb-2 text-start">
              <label className="form-label normal-text fw-bold text-dark opacity-75 text-uppercase mb-2" style={{ fontSize: '12px' }}>
                {view === 'register' ? 'Set Password' : 'Password'}
              </label>
              <div className="input-group border rounded-3 overflow-hidden shadow-sm">
                <span className="input-group-text bg-light border-0"><i className="bi bi-lock text-dark"></i></span>
                <input type={showPassword ? "text" : "password"} className="form-control border-0 shadow-none py-2 normal-text" 
                       value={password} onChange={(e) => setPassword(e.target.value)} disabled={loading} required />
                <span className="input-group-text bg-light border-0 cursor-pointer" onClick={() => setShowPassword(!showPassword)}>
                  <i className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'} text-dark`}></i>
                </span>
              </div>
            </div>
          )}

          {view === 'login' && (
            <div className="text-end mb-3">
               <button type="button" className="btn btn-link btn-sm p-0 text-decoration-none xsmall text-danger fw-bold" onClick={() => setView('forgot')}>Forgot Password?</button>
            </div>
          )}
          
          <button type="submit" className="theme-btn btn-danger w-100 py-2 mb-3 rounded-3 shadow-sm mt-2 text-uppercase" disabled={loading}>
            {loading ? <span className="spinner-border spinner-border-sm"></span> : 
             view === 'login' ? 'Login to Dashboard' : view === 'register' ? 'Create Driver Account' : 'Send Reset Link'}
          </button>

          {view === 'login' && (
            <>
              <div className="position-relative my-4">
                  <hr className="text-muted opacity-25" />
                  <span className="position-absolute top-50 start-50 translate-middle bg-white px-3 xsmall fw-bold text-muted">OR</span>
              </div>
              <button type="button" className="theme-btn btn-outline-dark w-100 rounded-3 py-2 mb-3 fw-bold d-flex align-items-center justify-content-center bg-transparent border-dark shadow-sm" onClick={handleGoogleLogin} disabled={loading} style={{ border: '1px solid #ddd' }}>
                <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" className="me-2" style={{width: '18px'}} alt="G" />
                Sign in with Google
              </button>
            </>
          )}

          <div className="text-center mt-3">
            <button type="button" className="btn btn-link btn-sm text-decoration-none fw-bold text-dark opacity-75 normal-text" 
                    onClick={() => {
                      setView(view === 'login' ? 'register' : 'login');
                      setError('');
                      setMessage('');
                    }} disabled={loading}>
              {view === 'login' ? "NEW DRIVER? REGISTER NOW" : "RETURN TO LOGIN SCREEN"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Login;